import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { ProgressBar } from "react-aria-components";
import { ImSpinner2 } from "react-icons/im";
import { css } from "../../../../styled-system/css";
import TEMPLATES from "./templates";
import { ProgramForm } from "./ProgramForm";
import { useProgram } from "../../../modules/programs/api/useProgram";
import { useUpdateProgram } from "../../../modules/programs/api/useUpdateProgram";
import { type ProgramFormValues } from "../../../modules/programs/programFormValues";
import { errorToast } from "../../../utils/toasts";

interface FormProps {
  id: number;
  templateType: string;
  defaultValues: ProgramFormValues;
  FormComponent: (typeof TEMPLATES)[number]["FormComponent"];
  onCancel: () => void;
  onSuccess: () => void;
}

const EditProgramForm = ({ id, templateType, defaultValues, FormComponent, onCancel, onSuccess }: FormProps) => {
  const { handleSubmit, ...form } = useForm<ProgramFormValues>({
    shouldUnregister: false,
    defaultValues,
    mode: "onChange",
  });

  const { t } = useTranslation(["programs"]);
  const updateProgram = useUpdateProgram({ id, templateType, onSuccess });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <ProgramForm
        submitLabel={t(($) => $.programs.footer.Update)}
        onSubmit={handleSubmit(
          (values) => updateProgram.mutate(values),
          () => errorToast({ message: t(($) => $.programs["Some fields are missing or invalid."]) }),
        )}
        onCancel={onCancel}
        isPending={updateProgram.isPending || form.formState.isSubmitting}
      >
        <FormComponent {...form} />
      </ProgramForm>
    </FormProvider>
  );
};

interface Props {
  programId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

export const EditProgram = ({ programId, ...props }: Props) => {
  const { t } = useTranslation(["common"]);
  const { data, isFetching } = useProgram(programId);

  const templateType = data?.template_type || "CUSTOM";
  const FormComponent = useMemo(
    () => (isFetching || !data ? null : (TEMPLATES.find((t) => t.id === templateType)?.FormComponent ?? null)),
    [isFetching, data, templateType],
  );

  if (!data || !FormComponent) {
    return (
      <ProgressBar
        aria-label={t(($) => $.common["Loading..."])}
        isIndeterminate
        className={css({ display: "flex", justifyContent: "center", padding: "large", fontSize: "xxlarge" })}
      >
        <ImSpinner2 className={css({ animation: "spin" })} />
      </ProgressBar>
    );
  }

  const { id, name, start_date, end_date, ui_state, spec } = data;
  return (
    <EditProgramForm
      id={id}
      templateType={templateType}
      FormComponent={FormComponent}
      defaultValues={{ name, start_date, end_date, ...(templateType === "CUSTOM" ? spec : ui_state) }}
      {...props}
    />
  );
};
